import { Head, router, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import {
  companyResourcePath,
  companyResourcesPath,
  deleteFileCompanyResourcePath,
  editCompanyResourcePath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface ResourceType {
  id: number
  name: string
}

interface ResourceForm {
  id: number
  name: string
  resource_type_id: number | null
  notes: string
  build: number | null
  status: number
  filename: string | null
  file_type: string | null
  file_url: string | null
  log: string | null
}

interface Props {
  resource: ResourceForm
  resource_types: ResourceType[]
  can_destroy?: boolean
  errors?: Record<string, string[]>
}

export default function Edit({
  resource,
  resource_types,
  can_destroy,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Company", href: companyResourcesPath() },
    { title: "Bios & Flyers", href: companyResourcesPath() },
    { title: "Edit Resource", href: editCompanyResourcePath(resource.id) },
  ]

  const { data, setData, patch, processing, transform } = useForm({
    name: resource.name ?? "",
    resource_type_id: resource.resource_type_id ?? "",
    notes: resource.notes ?? "",
    build: resource.build === 1,
    status: resource.status ?? 0,
    file: null as File | null,
  })

  transform((data) => ({ resource: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(companyResourcePath(resource.id), {
      forceFormData: true,
    })
  }

  const inputClass =
    "mt-1 block w-full rounded border border-hpi-light-gray px-3 py-2 focus:border-hpi-active-blue focus:outline-none"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Edit Resource" />

      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Update Resource</h1>
          {can_destroy && (
            <button
              type="button"
              onClick={() => {
                if (confirm("Are you sure you want to delete this resource?")) {
                  router.delete(companyResourcePath(resource.id))
                }
              }}
              className="bg-hpi-red rounded px-4 py-2 text-sm text-white hover:bg-red-700"
            >
              Delete Resource
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="max-w-2xl space-y-4">
          <div>
            <label className={labelClass}>Type</label>
            <select
              value={data.resource_type_id}
              onChange={(e) =>
                setData(
                  "resource_type_id",
                  e.target.value ? Number(e.target.value) : "",
                )
              }
              required
              className={inputClass}
            >
              <option value="">Select a type...</option>
              {resource_types.map((rt) => (
                <option key={rt.id} value={rt.id}>
                  {rt.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Title</label>
            <input
              type="text"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              maxLength={50}
              required
              className={inputClass}
            />
            {errors?.name && (
              <p className="text-hpi-red mt-1 text-sm">{errors.name[0]}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>Notes</label>
            <textarea
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              rows={5}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Status</label>
            <select
              value={data.status}
              onChange={(e) => setData("status", Number(e.target.value))}
              className={inputClass}
            >
              <option value={0}>Active</option>
              <option value={1}>Inactive</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="build"
              checked={!!data.build}
              onChange={(e) => setData("build", e.target.checked)}
              className="text-hpi-active-blue h-4 w-4 rounded border-gray-300"
            />
            <label htmlFor="build" className="text-hpi-dark-gray text-sm">
              Include in build list
            </label>
          </div>

          {resource.filename ? (
            <div>
              <label className={labelClass}>Current File</label>
              <div className="mt-1 flex items-center gap-2 text-sm">
                {resource.file_url ? (
                  <a
                    href={resource.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-hpi-active-blue hover:underline"
                  >
                    {resource.filename}
                  </a>
                ) : (
                  <span className="text-hpi-dark-gray">
                    {resource.filename}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (confirm("Are you sure you want to delete this file?")) {
                      router.delete(deleteFileCompanyResourcePath(resource.id))
                    }
                  }}
                  className="bg-hpi-warning ml-2 rounded px-2 py-1 text-xs text-white hover:bg-orange-600"
                >
                  Delete File
                </button>
              </div>
              <div className="mt-2">
                <label className="text-hpi-med-gray text-xs">
                  Replace file (optional)
                </label>
                <input
                  type="file"
                  onChange={(e) => setData("file", e.target.files?.[0] ?? null)}
                  className="text-hpi-med-gray file:bg-hpi-pale-blue file:text-hpi-dark-blue hover:file:bg-hpi-light-blue mt-1 block w-full text-sm file:mr-4 file:rounded file:border-0 file:px-4 file:py-2 file:text-sm file:font-semibold"
                />
              </div>
            </div>
          ) : (
            <div>
              <label className={labelClass}>Select File</label>
              <input
                type="file"
                onChange={(e) => setData("file", e.target.files?.[0] ?? null)}
                className="text-hpi-med-gray file:bg-hpi-pale-blue file:text-hpi-dark-blue hover:file:bg-hpi-light-blue mt-1 block w-full text-sm file:mr-4 file:rounded file:border-0 file:px-4 file:py-2 file:text-sm file:font-semibold"
              />
            </div>
          )}

          <div className="flex justify-end space-x-4">
            <a
              href={companyResourcesPath()}
              className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Cancel
            </a>
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              {processing ? "Saving..." : "Save Resource"}
            </button>
          </div>
        </form>

        {resource.log && (
          <div className="mt-6 max-w-2xl">
            <div className="rounded border border-gray-200 bg-gray-50 p-4">
              <h3 className="mb-2 text-sm font-semibold text-gray-700">
                Change Log
              </h3>
              <pre className="text-xs whitespace-pre-wrap text-gray-500">
                {resource.log}
              </pre>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
